import { Card } from "../../../../components/Card";
import CircularProgress from "../../../../components/CircularProgress";

import { PerformanceCard } from "./PerformanceCard";
import { getReportInsights } from "../../../../services/evalCategoryUtils";
import { AIOverview } from "../AIOverview";
import { TooltipView } from "../../../../components/Tooltip";
import { PerformanceView } from "../PerformanceView";
import { useApp } from "../../../../services/app-context";
import { useUser } from "../../../../services/user-context";
import { getObjectionTitle, getPersonalityLabels, getPractitionerTypes } from "../../../../services/utils";
import { formatCallFocusDisplay, hasTenantCallFocusTopics } from "../../../praxis-play/CustomizerModal";
import { XPReportView } from "../XPReportView";
import { InsightsOverviewCard } from "./InsightsOverviewCard";
import { InsightsCallDetailsCard } from "./InsightsCallDetailsCard";

import { isBadgeCenterEnabled, isCallFocusEnabled } from "../../../../integrations/posthog/featureFlags";
import { usePostHogFeatureFlag } from "../../../../integrations/posthog/usePostHogFeatureFlag";

export const filteredFastReportsMetrics = (metrics: any, metric_exclusions: any) => {
  if (Array.isArray(metrics)) {
    return metrics.filter((metric) => metric_exclusions?.[metric?.key]?.show !== false);
  }
  return Object.keys(metrics).filter((metric) => metric_exclusions?.[metric]?.show !== false);
}

export function CallInsight({
  title,
  percentage,
  radius = 40,
  strokeWidth = 6,
  customText,
}: {
  title: string;
  percentage: number;
  radius?: number;
  strokeWidth?: number;
  customText?: string;
}) {
  return (
    <div className="flex flex-col gap-4 items-center p-3 hover:bg-progressHover rounded">
      {title && (
        <span className="text-[15px] leading-[18px] whitespace-nowrap capitalize">
          {title}
        </span>
      )}
      <CircularProgress
        percentage={percentage}
        radius={radius}
        strokeWidth={strokeWidth}
        customText={customText}
      />
    </div>
  );
}

export const getStarCount = (score: number | string) => {
  if (score !== null && score !== undefined) {
    const number = Number(score);
    // remove decimal
    const integer = Math.floor(number);
    if (integer > 5) {
      return 5;
    }
    return integer;
  }
  return 0;
}

export function Performance({
  report,
  metrics,
  reportAverage,
}: {
  report: any;
  metrics: any;
  reportAverage: any;
}) {

  const isBadgeCenterEnabledFlag = usePostHogFeatureFlag(isBadgeCenterEnabled);
  const callFocusFlagOn = usePostHogFeatureFlag(isCallFocusEnabled);
  const { tenantProfile } = useApp();
  const { userInfo } = useUser() as any;
  const group = userInfo?.group;
  const metric_exclusions = tenantProfile?.metric_exclusions || {};
  const fastReportsMetricExclusions = metric_exclusions?.fast_reports || {};
  const metricsKeys = Object.keys(metrics).filter(
    (key) => !["score"].includes(key) && fastReportsMetricExclusions[key]?.show !== false
  );

  const metric_ranges = tenantProfile?.metric_ranges || [];
  const info_badges = tenantProfile?.info_badges || {};

  const highRange = metric_ranges?.find((range: any) => range.max === 100);
  const avgScore = reportAverage?.averagePraxisScore || 0;
  const avgScoreTrend = reportAverage?.averagePraxisScoreTrend || {};

  const improvementAreas = metricsKeys.filter(
    (key) => metrics[key]?.value < highRange?.min
  ).map((key) => ({ ...metrics[key], key })).sort((a, b) => a.value > b.value ? -1 : 1).slice(0, 3);

  const strengths = metricsKeys.filter(
    (key) => metrics[key]?.value >= highRange?.min
  ).map((key) => ({ ...metrics[key], key })).sort((a, b) => a.value > b.value ? -1 : 1).slice(0, 3);

  const score = report?.report?.reportScore || report?.session?.reportScore || 0;

  const reportInsights = getReportInsights(tenantProfile?.dashboard_insights?.metrics);
  const isPracticeSession = report?.session?.isUnscored || false;

  const practitionerTypes = getPractitionerTypes(tenantProfile?.practitioner_types || {}, group) || [];
  const pt = practitionerTypes.find(
    (t: { key?: string; type?: string }) =>
      t.key === report?.session?.practitionerType || t.type === report?.session?.practitionerType,
  );
  const hcp = pt?.type || pt?.key || report?.session?.practitionerType || "—";
  const personality = getPersonalityLabels(report?.session?.personality || report?.session?.personalityType, tenantProfile, group) || "—";
  const objection = getObjectionTitle(report?.session?.objectionLevel, tenantProfile?.objections || []) || report?.session?.objectionLevel || "—";
  const callFocusAvailable = callFocusFlagOn && hasTenantCallFocusTopics(tenantProfile, group);
  const practitionerTag = report?.session?.practitionerTag ?? pt?.tag;
  const callFocusText = callFocusAvailable && report?.session?.topics?.length
    ? formatCallFocusDisplay(tenantProfile, report.session.topics, group, practitionerTag)
    : "";

  return (
    <div className="flex flex-col md:flex-row gap-4 items-stretch">
      <div className="flex flex-[3] flex-col gap-4">
        <InsightsOverviewCard report={report} score={score} avgScore={avgScore} isPracticeSession={isPracticeSession} />
        <div>
          <AIOverview report={report} />
        </div>
        <div id="call-insights">
          <Card className="h-full !px-0">
            <div className="px-4 mb-4 text-sm font-semibold text-text-primary">
              Core Competency Insights
            </div>
            <div className="px-4 flex flex-col gap-2">
              {filteredFastReportsMetrics(reportInsights, fastReportsMetricExclusions).map((metric) => (
                <PerformanceView
                  key={metric}
                  title={reportInsights[metric] || metric}
                  value={metrics[metric]?.value || 0}
                />
              ))}
            </div>
          </Card>
        </div>
      </div>
      <div className="flex flex-[5] flex-col gap-4">
        <InsightsCallDetailsCard hcp={hcp} objection={objection} personality={personality} callFocusText={callFocusText} />
        {isBadgeCenterEnabledFlag && <div>
          <XPReportView sessionId={(report as any)?.session?.sessionID} />
        </div>}
        <div id="improvement-areas">
          <Card>
            <div className="mb-3 flex items-center gap-2">
              <span className="text-sm font-semibold text-text-primary">Improvement Areas</span>
              {!!info_badges?.improvement_areas && (
                <TooltipView content={info_badges?.improvement_areas} id="opportunities" />
              )}
            </div>
            <div className="flex flex-col gap-4">
              {improvementAreas.map((metric, index) => (
                <PerformanceCard
                  key={index}
                  title={`Improve ${reportInsights[metric.key] || metric.key}`}
                  description={metric.feedback}
                  percentage={metric.value || 0}
                />
              ))}
              {
                improvementAreas.length === 0 && (
                  <div className="flex items-center justify-center h-full py-4">
                    <span className="text-text-gray text-[15px] font-normal">No improvement areas found</span>
                  </div>
                )
              }
            </div>
          </Card>
        </div>
        <Card>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-sm font-semibold text-text-primary">Top Strengths</span>
            {!!info_badges?.top_strengths && (<TooltipView
              content={info_badges?.top_strengths}
              id="strengths"
            />)}
          </div>
          <div className="flex flex-col gap-4">
            {strengths.map((metric, index) => (
              <PerformanceCard
                key={index}
                title={reportInsights[metric.key] || metric.key}
                description={metric.feedback}
                percentage={metric.value || 0}
              />
            ))}
            {
              strengths.length === 0 && (
                <div className="flex items-center justify-center h-full py-4">
                  <span className="text-text-gray text-[15px] font-normal">No strengths found</span>
                </div>
              )
            }
          </div>
        </Card>
      </div>
    </div>
  );
}
